import axios from 'axios'
import {
    getEventDetailQuery,
    getGDPRQuery,
    getHallInfoDataQuery,
    getSeatsAndHallQuery,
    getSeatsQuery,
} from 'services/graphql/queries'
import { clearSession, getPersisted, setPersisted } from 'services/token/helpers'
import { maybeRefreshProactively, refreshToken } from 'services/token/refreshLock'
import config from '../../config'

const GQL_token = 'Bearer jAYsrBfTzqmdVDULOuYIYHbW3oov-VPp'

/**
 * axios for refresh
 * used only for token retrieval
 * separated from apiClient to prevent retry infinite loop (response interceptor)...
 */
const refreshClient = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    timeout: 10_000,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
})

refreshClient.interceptors.request.use(async (req) => {
    const api_token = getPersisted('token')
    const xClient = getPersisted('x-client')

    console.log('📡 [API] refreshClient request:', {
        url: req.url,
        token: api_token ? 'EXISTS' : 'MISSING',
        xClient: xClient ? 'EXISTS' : 'MISSING',
        headers: req.headers,
    })

    if (api_token) {
        req.headers.token = api_token
    }

    if (xClient) {
        req.headers['x-client'] = xClient
    }

    return req
})

/**
 * axios instance for all requests
 */
const apiClient = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    timeout: 10_000,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
})

apiClient.interceptors.request.use(async (req) => {
    // Obnovit token ještě před odesláním, když se blíží expirace. Requesty pak nepadají na
    // API_TOKEN_FAILED a reaktivní cesta (včetně destruktivního createToken fallbacku) se
    // skoro nespustí. Nikdy nevyhodí výjimku – request se odešle tak jako tak.
    await maybeRefreshProactively(() => REST_endpoints.refresh())

    const api_token = getPersisted('token')
    const xClient = getPersisted('x-client')

    console.log('📡 [API] apiClient request:', {
        url: req.url,
        token: api_token ? 'EXISTS' : 'MISSING',
        xClient: xClient ? 'EXISTS' : 'MISSING',
        headers: req.headers,
    })

    if (api_token) {
        req.headers.token = api_token
    }

    if (xClient) {
        req.headers['x-client'] = xClient
    }

    return req
})

apiClient.interceptors.response.use(
    async (response) => {
        const data = response.data
        const originalRequest = response.config

        // pokud BE nevrátil chybu tokenu, vrátit odpověď
        const isTokenError =
            (data?.statuscode === 500 && data?.code === 'API_TOKEN_FAILED') || data?.code === 'API_TOKEN_MISSING'
        if (!isTokenError) {
            return response
        }

        //retry if token fails
        originalRequest._retry = true

        const token = getPersisted('token')
        let newToken = null

        if (token) {
            console.log('🔄 [API] JWT expired, attempting refresh...')
            // Přes refreshToken, ne přímo – souběžné requesty tak spustí jeden refresh, ne N.
            newToken = await refreshToken(() => REST_endpoints.refresh())
        }

        // Refresh selhal nebo token nebyl – zkusit novou anonymní session (createToken)
        if (!newToken) {
            console.log('🔄 [API] Refresh failed or no token, trying createToken...')
            const uuid = crypto.randomUUID()
            setPersisted('x-client', uuid)
            try {
                const createResp = await REST_endpoints.createToken()
                if (createResp?.data?.token) {
                    newToken = createResp.data.token
                    setPersisted('token', newToken)
                    if (createResp.data.validity) {
                        setPersisted('token_validity', createResp.data.validity)
                    }
                }
            } catch (e) {
                console.error('❌ [API] createToken failed:', e)
            }
        }

        // Ani refresh ani createToken nevyšel – uživatel se musí znovu přihlásit → /login
        if (!newToken) {
            console.error('❌ [API] Refresh and createToken failed – redirecting to /login')
            originalRequest._retry = false
            clearSession()
            if (window.location.pathname !== '/error' && window.location.pathname !== '/login') {
                window.location.replace('/login')
            }
            return response
        }

        console.log('✅ [API] New token obtained, retrying request')

        const api_token = getPersisted('token')
        if (api_token) {
            originalRequest.headers.token = api_token
        }

        // retry
        return apiClient(originalRequest)
    },

    // errors
    (error) => Promise.reject(error)
)

const gqlClient = axios.create({
    baseURL: process.env.REACT_APP_CINESTAR_GRAPHQL_API_BASE_URL,
    timeout: 25000,
})

const gqlPost = (uri, query, headers = {}) => {
    return gqlClient.post(uri, { data: { query } }, { headers })
}

const gqlGet = (uri, query) => {
    return gqlClient.get(uri, { params: { query } })
}

const REST_endpoints = {
    healthCheck: () => apiClient.get(config.api.healthCheck.uri),

    createToken: () =>
        refreshClient.post(config.api.createToken.uri, {
            api_key: process.env.REACT_APP_API_KEY,
        }),

    refresh: () => refreshClient.get(config.api.refreshToken.uri),

    getTokenInfo: () => apiClient.get(config.api.getToken.uri),

    loginAuthorize: (params) => apiClient.post(config.api.login.uri, params),

    logoutAuthorize: () => apiClient.get(config.api.logout.uri),

    checkActiveAccount: (params) => apiClient.post(config.api.checkActiveAccount.uri, params),

    getAccount: () => apiClient.get(config.api.getAccount.uri),

    register: (params) => apiClient.post(config.api.register.uri, params),

    gdprSet: (params) => apiClient.post(config.api.gdprSet.uri, params),

    changePassword: (params) => apiClient.post(config.api.changePassword.uri, params),

    forgottenPassword: (params) => apiClient.post(config.api.forgottenPassword.uri, params),

    changeForgottenPassword: (params) => apiClient.post(config.api.changeForgottenPassword.uri, params),

    uploadProfileImage: (params) => apiClient.post(config.api.uploadProfileImage.uri, params),

    ordersGet: (params) => apiClient.post(config.api.ordersGet.uri, params),

    bookingsGet: (params) => apiClient.post(config.api.bookingsGet.uri, params),

    loyaltyProgramRegister: (params) => apiClient.post(config.api.loyaltyProgramRegister.uri, params),

    loyaltyProgramBlock: () => apiClient.get(config.api.loyaltyProgramBlock.uri),

    loyaltyProgramCoupons: () => apiClient.get(config.api.loyaltyProgramCoupons.uri),

    loyaltyProgramRewards: (params) => apiClient.post(config.api.loyaltyProgramRewards.uri, params),

    loyaltyProgramTransactions: () => apiClient.get(config.api.loyaltyProgramTransactions.uri),

    getEventData: (params) => apiClient.post(config.api.getEventData.uri, params),

    getRelatedEvents: (params) => apiClient.post(config.api.getRelatedEvents.uri, params),

    getHallData: (params) => apiClient.post(config.api.getHallData.uri, params),

    getBasketData: (params) => apiClient.post(config.api.getBasketData.uri, params),

    getVouchers: (params) => apiClient.post(config.api.getVouchers.uri, params),

    setVoucher: (params) => apiClient.post(config.api.setVouchers.uri, params),

    setCardProlonge: (params) => apiClient.post(config.api.cardProlonge.uri, params),

    setCardRemove: (params) => apiClient.delete(config.api.cardRemove.uri, { data: params }),

    addTicketsToBasket: (params) => apiClient.post(config.api.addTicketsToBasket.uri, params),

    removeTicketFromBasket: (params) => apiClient.delete(config.api.removeTicketFromBasket.uri, { data: params }),

    changeTicketCategory: (params) => apiClient.post(config.api.changeTicketCategory.uri, params),

    ticketVoucherAdd: (params) => apiClient.post(config.api.ticketVoucherAdd.uri, params),

    ticketVoucherRemove: (params) => apiClient.post(config.api.ticketVoucherRemove.uri, params),

    setPayment: (params) => apiClient.post(config.api.setPayment.uri, params),

    cartRelease: (params) => apiClient.delete(config.api.cartRelease.uri, { data: params }),

    // BOOKING

    bookingCreate: (params) => apiClient.post(config.api.bookingCreate.uri, params),

    bookingGet: (params) => apiClient.post(config.api.bookingGet.uri, params),

    bookingCancel: (params) => apiClient.post(config.api.bookingCancel.uri, params),

    orderGet: (params) => apiClient.post(config.api.orderGet.uri, params),

    bookingRestoreToCard: (params) => apiClient.post(config.api.bookingRestoreToCard.uri, params),

    // DOCUMENTS

    eticketGet: (params) => apiClient.post(config.api.eticketGet.uri, params),

    evoucherGet: (params) => apiClient.post(config.api.evoucherGet.uri, params),

    // SMS

    sendSms: (params) => apiClient.post(config.api.sendSms.uri, params),

    // PAYMENT

    paymentCreate: (params) => apiClient.post(config.api.paymentCreate.uri, params),

    paymentVerify: (params) => apiClient.post(config.api.paymentVerify.uri, params),

    paymentCancel: (params) => apiClient.delete(config.api.paymentCancel.uri, { data: params }),

    ticketPointPayment: (params) => apiClient.post(config.api.ticketPointPayment.uri, params),

    ticketMoneyPayment: (params) => apiClient.post(config.api.ticketMoneyPayment.uri, params),
}

const GQL_endpoints = {
    getGqlEventData: ({ movieId, site }) => {
        const query = getEventDetailQuery(movieId, site)

        return gqlGet(config.cinestarGql.getGqlEventData.uri, query)
    },

    getGqlSeatsDetails: ({ site }) => {
        const query = getSeatsAndHallQuery(site)
        return gqlGet(config.cinestarGql.getGqlEventData.uri, query)
    },

    getGqlHallDetail: ({ site, venueid }) => {
        const query = getHallInfoDataQuery(site, venueid)
        return gqlGet(config.cinestarGql.getGqlEventData.uri, query)
    },

    getSeats: ({ site, cinemaHallId }) => {
        const query = getSeatsQuery(site, cinemaHallId)
        return gqlGet(config.cinestarGql.getGqlEventData.uri, query)
    },

    getGDPRdata: (slug) => {
        const query = getGDPRQuery(slug)
        return gqlGet(config.cinestarGql.getGqlEventData.uri, query)
    },
}

export const all_endpoints = { ...REST_endpoints, ...GQL_endpoints }
